import React, { useState, useEffect, useMemo } from 'react';
import { Card, Container, Row, Col, Button, Modal, Form, ListGroup, Alert, InputGroup, Badge, Nav, Table } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, onValue, set, push, remove } from 'firebase/database';
import { FaEdit, FaTrash, FaPlus, FaGripVertical, FaTimes, FaCalendarAlt, FaListUl, FaDumbbell, FaSearch } from 'react-icons/fa'; 
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  UniqueIdentifier
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link, useLocation } from 'react-router-dom'; 

// Interfaces
interface Exercise {
  id: string;
  name: string;
  description?: string;
  category?: string; 
  defaultSets?: number;
  defaultReps?: number;
  defaultWeight?: number;
  notes?: string;
}

interface WorkoutTemplateExercise extends Exercise { 
  order: number; 
}

interface WorkoutTemplate {
  id: string;
  name: string;
  description?: string;
  exercises: WorkoutTemplateExercise[]; 
}

// SortableItem Component
interface SortableItemProps {
  id: UniqueIdentifier; 
  children: React.ReactNode;
  isDragging?: boolean; 
}

function SortableItem(props: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({id: props.id});
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    padding: '8px',
    border: '1px solid #eee',
    marginBottom: '4px',
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: '4px',
  };
  
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <span {...listeners} style={{ cursor: 'grab', marginRight: '8px', touchAction: 'none' }}><FaGripVertical /></span>
      {props.children}
    </div>
  );
}

const WorkoutExercises: React.FC = () => {
  const { currentUser } = useAuth();
  const location = useLocation();

  const [exercises, setExercises] = useState<Exercise[]>([]); 
  const [filteredExercises, setFilteredExercises] = useState<Exercise[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  const [showModal, setShowModal] = useState(false);
  const [currentExercise, setCurrentExercise] = useState<Partial<Exercise>>({ name: '', category: '', defaultSets: 3, defaultReps: 10, defaultWeight: 0, notes: '' });
  const [isEditing, setIsEditing] = useState(false);

  // Workout Templates state
  const [workoutTemplates, setWorkoutTemplates] = useState<WorkoutTemplate[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState<WorkoutTemplate>({ id: '', name: '', description: '', exercises: [] });
  
  const [exerciseAlertMessage, setExerciseAlertMessage] = useState<{ type: string, message: string } | null>(null);
  const [templateAlertMessage, setTemplateAlertMessage] = useState<{ type: string, message: string } | null>(null);

  // State for delete confirmation modals
  const [showDeleteExerciseModal, setShowDeleteExerciseModal] = useState(false);
  const [exerciseToDeleteId, setExerciseToDeleteId] = useState<string | null>(null);
  const [showDeleteTemplateModal, setShowDeleteTemplateModal] = useState(false);
  const [templateToDeleteId, setTemplateToDeleteId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Add activation constraint for better touch handling
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;
    
    if (over && active.id !== over.id) {
      setCurrentTemplate((prev) => {
        const oldIndex = prev.exercises.findIndex(ex => ex.id === active.id.toString());
        const newIndex = prev.exercises.findIndex(ex => ex.id === over.id.toString());
        
        if (oldIndex === -1 || newIndex === -1) return prev;

        let reorderedExercises = arrayMove(prev.exercises, oldIndex, newIndex);
        const finalExercises = reorderedExercises.map((ex, index) => ({ ...ex, order: index }));
        
        return {...prev, exercises: finalExercises};
      });
    }
  };

  const handleToggleExerciseInTemplate = (exercise: Exercise, checked: boolean) => {
    setCurrentTemplate(prev => {
      let updatedExercises;
      if (checked) {
        if (!prev.exercises.some(e => e.id === exercise.id)) {
          const exerciseToAdd: WorkoutTemplateExercise = { 
            ...exercise, 
            order: prev.exercises.length 
          };
          updatedExercises = [...prev.exercises, exerciseToAdd];
        } else {
          updatedExercises = prev.exercises; 
        }
      } else {
        updatedExercises = prev.exercises.filter(e => e.id !== exercise.id);
      }
      const finalExercises = updatedExercises.map((item, index) => ({ ...item, order: index }));
      return { ...prev, exercises: finalExercises };
    });
  };

  const removeExerciseFromTemplate = (exerciseId: string) => {
    setCurrentTemplate(prev => {
      const updatedExercises = prev.exercises.filter(e => e.id !== exerciseId);
      const finalExercises = updatedExercises.map((item, index) => ({ ...item, order: index }));
      return { ...prev, exercises: finalExercises };
    });
  };

  useEffect(() => {
    if (!currentUser) return;

    const exercisesRef = ref(database, `workouts/${currentUser.uid}/exercises`);
    const unsubscribeExercises = onValue(exercisesRef, (snapshot) => {
      const data = snapshot.val() || {};
      const exercisesList = Object.entries(data).map(([id, exercise]: [string, any]) => ({
        id,
        ...exercise
      })).sort((a, b) => a.name.localeCompare(b.name));

      setExercises(exercisesList);
      setFilteredExercises(exercisesList);

      const uniqueCategories = Array.from(new Set(exercisesList.map(ex => ex.category))).sort();
      setCategories(uniqueCategories);
    });

    const templatesRef = ref(database, `workouts/${currentUser.uid}/workout-templates`);
    const unsubscribeTemplates = onValue(templatesRef, (snapshot) => {
      const data = snapshot.val() || {};
      const templatesList: WorkoutTemplate[] = Object.keys(data).map(id => {
        const template = data[id];
        return {
          id,
          name: template.name || '',
          description: template.description || '',
          exercises: template.exercises 
            ? (Object.values(template.exercises) as WorkoutTemplateExercise[]).map((ex, idx) => ({
                ...ex,
                // Ensure 'id' is present and is a string, default if necessary (though ex.id should come from master exercise list)
                id: ex.id || `generated-id-${idx}`, // Fallback, ideally exercise ID is always there
                name: ex.name || 'Unnamed Exercise', // Fallback
                order: ex.order !== undefined ? ex.order : idx // Assign default order if missing
              })) 
            : []
        };
      }).sort((a, b) => a.name.localeCompare(b.name));
      setWorkoutTemplates(templatesList);
    });

    return () => {
      unsubscribeExercises();
      unsubscribeTemplates();
    };
  }, [currentUser]);

  useEffect(() => {
    let result = [...exercises];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(exercise =>
        exercise.name.toLowerCase().includes(term) ||
        exercise.notes?.toLowerCase().includes(term)
      );
    }

    if (categoryFilter !== 'all') {
      result = result.filter(exercise => exercise.category === categoryFilter);
    }

    setFilteredExercises(result);
  }, [exercises, searchTerm, categoryFilter]);

  const handleAddExercise = () => {
    setIsEditing(false);
    setCurrentExercise({
      id: '',
      name: '',
      category: categories.length > 0 ? categories[0] : '',
      defaultSets: 3,
      defaultReps: 10,
      defaultWeight: 0
    });
    setShowModal(true);
  };

  const handleEditExercise = (exercise: Exercise) => {
    setIsEditing(true);
    setCurrentExercise(exercise);
    setShowModal(true);
  };

  const handleSaveExercise = async () => {
    if (!currentUser || 
        !currentExercise.name || 
        !currentExercise.name.trim() || 
        !currentExercise.category || 
        !currentExercise.category.trim()
    ) {
      setExerciseAlertMessage({
        type: 'danger',
        message: 'Name und Kategorie sind erforderlich und dürfen nicht leer sein.'
      });
      return;
    }

    try {
      let exerciseRef;

      if (isEditing) {
        exerciseRef = ref(database, `workouts/${currentUser.uid}/exercises/${currentExercise.id}`);
        await set(exerciseRef, {
          name: currentExercise.name,
          category: currentExercise.category,
          defaultSets: currentExercise.defaultSets || 3,
          defaultReps: currentExercise.defaultReps || 10,
          defaultWeight: currentExercise.defaultWeight || 0,
          notes: currentExercise.notes || ''
        });
      } else {
        exerciseRef = ref(database, `workouts/${currentUser.uid}/exercises`);
        await push(exerciseRef, {
          name: currentExercise.name,
          category: currentExercise.category,
          defaultSets: currentExercise.defaultSets || 3,
          defaultReps: currentExercise.defaultReps || 10,
          defaultWeight: currentExercise.defaultWeight || 0,
          notes: currentExercise.notes || ''
        });
      }

      setShowModal(false);
      setExerciseAlertMessage({
        type: 'success',
        message: `Übung erfolgreich ${isEditing ? 'aktualisiert' : 'hinzugefügt'}.`
      });
      setTimeout(() => setExerciseAlertMessage(null), 3000);
    } catch (error) {
      console.error('Error saving exercise:', error);
      setExerciseAlertMessage({
        type: 'danger',
        message: `Fehler beim ${isEditing ? 'Aktualisieren' : 'Hinzufügen'} der Übung.`
      });
    }
  };

  const handleDeleteExercise = (id: string) => {
    const exercise = exercises.find(ex => ex.id === id);
    if (exercise) {
      setExerciseToDeleteId(id);
      setShowDeleteExerciseModal(true);
    }
  };

  const confirmDeleteExercise = async () => {
  if (!currentUser || !exerciseToDeleteId) return;
  try {
    await remove(ref(database, `workouts/${currentUser.uid}/exercises/${exerciseToDeleteId}`));
    setExercises(prev => prev.filter(ex => ex.id !== exerciseToDeleteId));
    setExerciseAlertMessage({ type: 'success', message: 'Übung erfolgreich gelöscht.' });
    // Also remove from any templates
    const updatedTemplates = workoutTemplates.map(template => {
      const newExercises = template.exercises.filter(ex => ex.id !== exerciseToDeleteId);
      if (newExercises.length !== template.exercises.length) {
        // If exercises were removed, update the template in Firebase
        set(ref(database, `workouts/${currentUser.uid}/workout-templates/${template.id}`), { ...template, exercises: newExercises });
        return { ...template, exercises: newExercises };
      }
      return template;
    });
    setWorkoutTemplates(updatedTemplates);

  } catch (error) {
    console.error("Error deleting exercise: ", error);
    setExerciseAlertMessage({ type: 'danger', message: 'Fehler beim Löschen der Übung.' });
  }
  setShowDeleteExerciseModal(false);
  setExerciseToDeleteId(null);
};

  const handleAddCategory = () => {
    const newCategory = prompt('Neue Kategorie eingeben:');
    if (newCategory && newCategory.trim()) {
      setCurrentExercise({ ...currentExercise, category: newCategory.trim() });
    }
  };

  const handleAddTemplate = () => {
    setIsEditingTemplate(false);
    setCurrentTemplate({
      id: '',
      name: '',
      description: '',
      exercises: []
    });
    setShowTemplateModal(true);
  };

  const handleEditTemplate = (template: WorkoutTemplate) => {
    setIsEditingTemplate(true);
    setCurrentTemplate({ ...template }); 
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!currentUser || !currentTemplate.name.trim()) {
      setTemplateAlertMessage({ type: 'danger', message: 'Name ist erforderlich.' });
      return;
    }
    if (!currentTemplate.exercises.length) {
      setTemplateAlertMessage({ type: 'danger', message: 'Mindestens eine Übung auswählen.' });
      return;
    }
    try {
      let templateRef;
      if (isEditingTemplate && currentTemplate.id) {
        templateRef = ref(database, `workouts/${currentUser.uid}/workout-templates/${currentTemplate.id}`);
        await set(templateRef, {
          name: currentTemplate.name,
          description: currentTemplate.description || '',
          exercises: currentTemplate.exercises
        });
      } else {
        templateRef = ref(database, `workouts/${currentUser.uid}/workout-templates`);
        await push(templateRef, {
          name: currentTemplate.name,
          description: currentTemplate.description || '',
          exercises: currentTemplate.exercises
        });
      }
      setShowTemplateModal(false);
      setTemplateAlertMessage({ type: 'success', message: `Vorlage erfolgreich ${isEditingTemplate ? 'aktualisiert' : 'hinzugefügt'}.` });
      setTimeout(() => setTemplateAlertMessage(null), 3000);
    } catch (error) {
      setTemplateAlertMessage({ type: 'danger', message: 'Fehler beim Speichern der Vorlage.' });
    }
  };

  const handleDeleteTemplate = (id: string) => {
    const template = workoutTemplates.find(t => t.id === id);
    if (template) {
      setTemplateToDeleteId(id);
      setShowDeleteTemplateModal(true);
    }
  };

  const confirmDeleteTemplate = async () => {
    if (!currentUser || !templateToDeleteId) return;
    try {
      await remove(ref(database, `workouts/${currentUser.uid}/workout-templates/${templateToDeleteId}`));
      setWorkoutTemplates(prev => prev.filter(template => template.id !== templateToDeleteId));
      setTemplateAlertMessage({ type: 'success', message: 'Vorlage erfolgreich gelöscht.' });
    } catch (error) {
      console.error("Error deleting template: ", error);
      setTemplateAlertMessage({ type: 'danger', message: 'Fehler beim Löschen der Vorlage.' });
    }
    setShowDeleteTemplateModal(false);
    setTemplateToDeleteId(null);
  };

  if (!currentUser) {
    return (
      <Container>
        <h2 className="mb-4">Übungen</h2>
        <Card>
          <Card.Body>
            <Card.Title style={{ color: '#1780a6' }}>Übungen</Card.Title>
            <p>Bitte melden Sie sich an, um Ihre Übungen anzuzeigen.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <h2 className="mb-4">Übungen</h2>

      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link
            as={Link}
            to="/dashboard/workouts"
            active={location.pathname === '/dashboard/workouts'}
          >
            <FaCalendarAlt className="me-2" />
            Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            as={Link}
            to="/dashboard/workouts-list"
            active={location.pathname === '/dashboard/workouts-list'}
          >
            <FaListUl className="me-2" />
            Workout Liste
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            as={Link}
            to="/dashboard/workout-exercises"
            active={location.pathname === '/dashboard/workout-exercises'}
          >
            <FaDumbbell className="me-2" />
            Übungen
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <Card className="mb-4">
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }} className="d-flex justify-content-between align-items-center mb-4">
            <span>Übungen</span>
            <Button
              variant="primary"
              onClick={handleAddExercise}
            >
              <FaPlus className="me-2" />
              Neue Übung
            </Button>
          </Card.Title>
          
          {exerciseAlertMessage && (
            <Alert
              variant={exerciseAlertMessage.type}
              onClose={() => setExerciseAlertMessage(null)}
              dismissible
              className="mb-4"
            >
              {exerciseAlertMessage.message}
            </Alert>
          )}

          <Row className="mb-4">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Übung suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={6}>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">Alle Kategorien</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          {filteredExercises.length > 0 ? (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Kategorie</th>
                  <th>Standard Werte</th>
                  <th>Notizen</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredExercises.map(exercise => (
                  <tr key={exercise.id}>
                    <td>{exercise.name}</td>
                    <td>
                      <Badge bg="secondary">{exercise.category}</Badge>
                    </td>
                    <td>
                      <div className="small">
                        <span className="me-2">{exercise.defaultSets || 3} Sätze</span>
                        <span className="me-2">{exercise.defaultReps || 10} Wdh.</span>
                        <span>{exercise.defaultWeight || 0} kg</span>
                      </div>
                    </td>
                    <td>
                      {exercise.notes && (
                        <div className="small text-muted">
                          {exercise.notes.length > 30
                            ? `${exercise.notes.substring(0, 30)}...`
                            : exercise.notes}
                        </div>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEditExercise(exercise)}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteExercise(exercise.id)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Alert variant="info">
              Keine Übungen gefunden. {searchTerm || categoryFilter !== 'all' ? (
                <Button
                  variant="link"
                  className="p-0"
                  onClick={() => {
                    setSearchTerm('');
                    setCategoryFilter('all');
                  }}
                >
                  Filter zurücksetzen
                </Button>
              ) : (
                <Button
                  variant="link"
                  className="p-0"
                  onClick={handleAddExercise}
                >
                  Neue Übung hinzufügen
                </Button>
              )}
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }} className="d-flex justify-content-between align-items-center mb-4">
            <span>Workout-Vorlagen</span>
            <Button variant="primary" onClick={handleAddTemplate}>
              <FaPlus className="me-2" /> Neue Vorlage
            </Button>
          </Card.Title>

          {templateAlertMessage && (
            <Alert variant={templateAlertMessage.type} onClose={() => setTemplateAlertMessage(null)} dismissible className="mb-4">
              {templateAlertMessage.message}
            </Alert>
          )}

          {workoutTemplates.length > 0 ? (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Übungen</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {workoutTemplates.map(template => (
                  <tr key={template.id}>
                    <td>{template.name}</td>
                    <td>
                      <div className="small">
                        {template.exercises && template.exercises.length > 0
                          ? template.exercises.map(ex => ex.name).join(', ')
                          : <span className="text-muted">Keine Übungen</span>}
                      </div>
                    </td>
                    <td>
                      <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleEditTemplate(template)}>
                        <FaEdit />
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDeleteTemplate(template.id)}>
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Alert variant="info">
              Keine Vorlagen gefunden.
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? 'Übung bearbeiten' : 'Neue Übung hinzufügen'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={currentExercise.name}
                onChange={(e) => setCurrentExercise({ ...currentExercise, name: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Kategorie</Form.Label>
              <InputGroup>
                <Form.Select
                  value={currentExercise.category}
                  onChange={(e) => setCurrentExercise({ ...currentExercise, category: e.target.value })}
                >
                  <option value="">Kategorie wählen</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </Form.Select>
                <Button
                  variant="outline-secondary"
                  onClick={handleAddCategory}
                >
                  Neu
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Standard Sätze</Form.Label>
              <Form.Control
                type="number"
                value={currentExercise.defaultSets || 3}
                onChange={(e) => setCurrentExercise({
                  ...currentExercise,
                  defaultSets: parseInt(e.target.value) || 3
                })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Standard Wiederholungen</Form.Label>
              <Form.Control
                type="number"
                value={currentExercise.defaultReps || 10}
                onChange={(e) => setCurrentExercise({
                  ...currentExercise,
                  defaultReps: parseInt(e.target.value) || 10
                })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Standard Gewicht (kg)</Form.Label>
              <Form.Control
                type="number"
                step="0.5"
                value={currentExercise.defaultWeight || 0}
                onChange={(e) => setCurrentExercise({
                  ...currentExercise,
                  defaultWeight: parseFloat(e.target.value) || 0
                })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notizen</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={currentExercise.notes || ''}
                onChange={(e) => setCurrentExercise({
                  ...currentExercise,
                  notes: e.target.value
                })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleSaveExercise}>
            {isEditing ? 'Aktualisieren' : 'Hinzufügen'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditingTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage hinzufügen'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={currentTemplate.name}
                onChange={e => setCurrentTemplate({ ...currentTemplate, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Beschreibung (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={currentTemplate.description || ''}
                onChange={e => setCurrentTemplate({ ...currentTemplate, description: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Verfügbare Übungen auswählen</Form.Label>
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4, padding: 8 }}>
                {exercises.sort((a,b) => a.name.localeCompare(b.name)).map(ex => (
                  <Form.Check
                    type="checkbox"
                    key={`master-ex-${ex.id}`}
                    id={`master-ex-${ex.id}`}
                    label={<>{ex.name} {ex.category && <Badge pill bg="secondary" className="ms-3">{ex.category}</Badge>}</>}
                    checked={currentTemplate.exercises.some(e => e.id === ex.id)}
                    onChange={(e) => handleToggleExerciseInTemplate(ex, e.target.checked)}
                  />
                ))}
                {exercises.length === 0 && <div className="text-muted">Keine Übungen vorhanden.</div>}
              </div>
            </Form.Group>

            {currentTemplate.exercises.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>Reihenfolge der Übungen in der Vorlage (Drag & Drop)</Form.Label>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={currentTemplate.exercises.map(ex => ex.id)} strategy={verticalListSortingStrategy}>
                    <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: '8px' }}>
                      {currentTemplate.exercises.sort((a, b) => a.order - b.order).map(templateExercise => (
                        <SortableItem key={templateExercise.id} id={templateExercise.id}>
                          <span>{templateExercise.name}</span>
                          <Button variant="link" size="sm" onClick={() => removeExerciseFromTemplate(templateExercise.id)} title="Aus Vorlage entfernen">
                            <FaTimes style={{color: 'red'}} />
                          </Button>
                        </SortableItem>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTemplateModal(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleSaveTemplate}>
            {isEditingTemplate ? 'Aktualisieren' : 'Hinzufügen'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Exercise Confirmation Modal */}
      <Modal show={showDeleteExerciseModal} onHide={() => setShowDeleteExerciseModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Übung löschen bestätigen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Möchten Sie die Übung "<strong>{exercises.find(ex => ex.id === exerciseToDeleteId)?.name || ''}</strong>" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteExerciseModal(false)}>
            Abbrechen
          </Button>
          <Button variant="danger" onClick={confirmDeleteExercise}>
            Löschen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Template Confirmation Modal */}
      <Modal show={showDeleteTemplateModal} onHide={() => setShowDeleteTemplateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Vorlage löschen bestätigen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Möchten Sie die Vorlage "<strong>{workoutTemplates.find(t => t.id === templateToDeleteId)?.name || ''}</strong>" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteTemplateModal(false)}>
            Abbrechen
          </Button>
          <Button variant="danger" onClick={confirmDeleteTemplate}>
            Löschen
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default WorkoutExercises;
